import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { CreditCard, Shield, CheckCircle, Loader2, MapPin, Users, Flame } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CardRequiredModal() {
  const [loading, setLoading] = useState(null);
  const [activeTab, setActiveTab] = useState("online"); // online or inhouse

  const handleSetupCard = async (planId) => {
    setLoading(planId);
    try {
      const response = await axios.post(`${API}/payments/setup-card`, {
        plan_id: planId,
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      toast.error("Failed to setup payment");
      setLoading(null);
    }
  };

  const handleInHousePayment = async (planId) => {
    setLoading(planId);
    try {
      const response = await axios.post(`${API}/payments/checkout`, {
        plan_id: planId,
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      toast.error("Failed to setup payment");
      setLoading(null);
    }
  };

  const onlinePlans = [
    { id: "basic", name: "Basic", price: "$9.99/mo", trial: "$1 to start" },
    { id: "pro", name: "Pro", price: "$49.99/mo", trial: "$1 to start", popular: true },
    { id: "elite", name: "Elite", price: "$399/mo", trial: "$1 to start" }
  ];

  const inHousePlans = [
    { id: "dropin", name: "Drop-In Class", price: "$10", desc: "Single class, no commitment" },
    { id: "inhouse_3day", name: "3-Day Weekly", price: "$65/mo", desc: "Mon/Wed/Fri boxing classes" },
    { id: "inhouse_weekend", name: "Weekend Warrior", price: "$70/mo", desc: "Sat burnout + Sun boxing" },
    { id: "coaching", name: "Coach Coop Coaching", price: "$120/mo", desc: "Boot camp + 1-on-1 coaching", popular: true }
  ];

  return (
    <div className="fixed inset-0 z-[100] bg-background flex items-center justify-center p-4 overflow-y-auto">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-lg my-8"
      >
        <Card className="bg-card border-primary/30">
          <CardContent className="p-6 sm:p-8">
            {/* Header */}
            <div className="text-center mb-6">
              <motion.div
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/20 flex items-center justify-center"
              >
                <CreditCard className="h-8 w-8 text-primary" />
              </motion.div>
              <h2 className="text-2xl font-black uppercase tracking-tighter mb-2">
                Choose Your Plan
              </h2>
              <p className="text-muted-foreground text-sm">
                Online programs or in-house boxing classes
              </p>
            </div>

            {/* Tab Switcher */}
            <div className="flex gap-2 mb-6 bg-secondary rounded-lg p-1">
              <Button
                variant={activeTab === "online" ? "default" : "ghost"}
                onClick={() => setActiveTab("online")}
                className={`flex-1 ${activeTab === "online" ? "bg-primary" : ""}`}
              >
                <Flame className="h-4 w-4 mr-2" />
                Online
              </Button>
              <Button
                variant={activeTab === "inhouse" ? "default" : "ghost"}
                onClick={() => setActiveTab("inhouse")}
                className={`flex-1 ${activeTab === "inhouse" ? "bg-orange-500 hover:bg-orange-600" : ""}`}
              >
                <MapPin className="h-4 w-4 mr-2" />
                🥊 In-House
              </Button>
            </div>

            {/* Online Plans */}
            {activeTab === "online" && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-3 mb-6"
              >
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">
                  Online Membership - 7-Day Trial for $1
                </p>
                {onlinePlans.map((plan) => (
                  <Button
                    key={plan.id}
                    onClick={() => handleSetupCard(plan.id)}
                    disabled={loading !== null}
                    variant={plan.popular ? "default" : "outline"}
                    className={`w-full justify-between py-6 ${
                      plan.popular 
                        ? 'bg-primary hover:bg-primary/90' 
                        : 'border-border hover:bg-secondary'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {loading === plan.id ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        <CreditCard className="h-5 w-5" />
                      )}
                      <div className="text-left">
                        <div className="font-bold">{plan.name}</div>
                        <div className="text-xs opacity-70">{plan.price}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold text-accent">{plan.trial}</div>
                      {plan.popular && <div className="text-[10px] opacity-70">POPULAR</div>}
                    </div>
                  </Button>
                ))}

                {/* Trial Info */}
                <div className="bg-secondary/30 rounded-lg p-3 mt-4">
                  <div className="flex items-start gap-3">
                    <Shield className="h-4 w-4 text-accent mt-0.5" />
                    <div className="text-xs">
                      <p className="font-bold text-accent">7-Day Trial Guarantee</p>
                      <p className="text-muted-foreground">Cancel anytime. No questions asked.</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* In-House Plans */}
            {activeTab === "inhouse" && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-3 mb-6"
              >
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">
                  In-House Boxing Classes - Pay Now
                </p>
                {inHousePlans.map((plan) => (
                  <Button
                    key={plan.id}
                    onClick={() => handleInHousePayment(plan.id)}
                    disabled={loading !== null}
                    variant={plan.popular ? "default" : "outline"}
                    className={`w-full justify-between py-6 ${
                      plan.popular 
                        ? 'bg-orange-500 hover:bg-orange-600' 
                        : 'border-orange-500/30 hover:bg-orange-500/10'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {loading === plan.id ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        <Users className="h-5 w-5" />
                      )}
                      <div className="text-left">
                        <div className="font-bold">{plan.name}</div>
                        <div className="text-xs opacity-70">{plan.desc}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold">{plan.price}</div>
                      {plan.popular && <div className="text-[10px] opacity-70">BEST VALUE</div>}
                    </div>
                  </Button>
                ))}

                {/* In-House Info */}
                <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3 mt-4">
                  <div className="flex items-start gap-3">
                    <MapPin className="h-4 w-4 text-orange-500 mt-0.5" />
                    <div className="text-xs">
                      <p className="font-bold text-orange-500">Boxing & Conditioning</p>
                      <p className="text-muted-foreground">Pay online, train at our gym. We'll send location details.</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Trust Badges */}
            <div className="flex items-center justify-center gap-3 text-xs text-muted-foreground pt-4 border-t border-border">
              <div className="flex items-center gap-1">
                <CheckCircle className="h-3 w-3 text-accent" />
                <span>Secure</span>
              </div>
              <div className="flex items-center gap-1">
                <CheckCircle className="h-3 w-3 text-accent" />
                <span>Cancel Anytime</span>
              </div>
              <div className="flex items-center gap-1">
                <CheckCircle className="h-3 w-3 text-accent" />
                <span>Stripe Protected</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
